import React, { useEffect, useMemo, useState } from 'react';
import api, { fetchAllPages } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Search, Lock, RefreshCw, Database, ShieldCheck, AlertTriangle, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { VersionLifecycleCard } from './NdisVersionLifecycle';
import { toast } from 'sonner';

const TODAY = new Date().toISOString().slice(0, 10);
const PAGE_SIZE = 25;
const ALL = '__all__';

const pricingType = (it) => (it.unit_price_one ? 'Unit Price = $1' : it.quote ? 'Quotable' : 'Priced');
const effStatus = (it) => {
  if (it.start_date && it.start_date > TODAY) return 'FUTURE';
  if (!it.end_date_open_ended && it.end_date && it.end_date < TODAY) return 'EXPIRED';
  return 'ACTIVE';
};
const money = (v) => (v === null || v === undefined || v === '' ? '—' : `$${Number(v).toFixed(2)}`);

export default function NdisMasterCatalogue() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const [itemClass, setItemClass] = useState('CURRENT');
  const [items, setItems] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(ALL);
  const [regGroup, setRegGroup] = useState(ALL);
  const [unit, setUnit] = useState(ALL);
  const [pricing, setPricing] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [page, setPage] = useState(1);

  const loadItems = async (cls) => {
    setLoading(true);
    setError(null);
    try {
      const all = await fetchAllPages('/ndis-catalogue/items', { item_class: cls });
      setItems(all);
    } catch (e) {
      setError('Failed to load the NDIS master catalogue.');
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    try {
      const res = await api.get('/ndis-catalogue/versions');
      setVersions(res.data.data || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { loadVersions(); }, []);
  useEffect(() => { loadItems(itemClass); setPage(1); }, [itemClass]);
  useEffect(() => { setPage(1); }, [search, category, regGroup, unit, pricing, status]);

  const distinct = (key) => {
    const s = new Set();
    items.forEach((it) => { const v = it[key]; if (v) s.add(v); });
    return Array.from(s).sort();
  };
  const categories = useMemo(() => distinct('support_category_name'), [items]);
  const regGroups = useMemo(() => distinct('registration_group_name'), [items]);
  const units = useMemo(() => distinct('unit'), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (q && !(`${it.support_item_number} ${it.support_item_name}`.toLowerCase().includes(q))) return false;
      if (category !== ALL && it.support_category_name !== category) return false;
      if (regGroup !== ALL && it.registration_group_name !== regGroup) return false;
      if (unit !== ALL && it.unit !== unit) return false;
      if (pricing !== ALL && pricingType(it) !== pricing) return false;
      if (status !== ALL && effStatus(it) !== status) return false;
      return true;
    });
  }, [items, search, category, regGroup, unit, pricing, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const version = versions.find((v) => v.status === 'PUBLISHED') || null;
  const canPublish = isSuperAdmin && (user?.permissions || []).includes('catalogue.publish');

  const runImport = async () => {
    setImporting(true);
    try {
      const res = await api.post('/ndis-catalogue/import');
      const d = res.data.data;
      toast.success(d.status === 'DRAFT_CREATED'
        ? `Draft created (${d.counts?.total} items) — submit for review and maker-checker approval`
        : `Already imported — ${d.items_present ?? d.counts?.total} items present (${d.version_status})`);
      await loadVersions();
      await loadItems(itemClass);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const resetFilters = () => { setSearch(''); setCategory(ALL); setRegGroup(ALL); setUnit(ALL); setPricing(ALL); setStatus(ALL); };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ndis-master-catalogue-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Database className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">NDIS Master Catalogue</h1>
            <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700 bg-amber-50" data-testid="ndis-readonly-badge">
              <Lock className="h-3 w-3" /> Read-only
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1">Official NDIS price guide — protected national master. Published versions are immutable.</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={runImport} disabled={importing} data-testid="ndis-import-button">
            <RefreshCw className={`h-4 w-4 mr-2 ${importing ? 'animate-spin' : ''}`} />
            {importing ? 'Importing…' : 'Import / Re-check master'}
          </Button>
        )}
      </div>

      {isSuperAdmin && <VersionLifecycleCard versions={versions} canPublish={canPublish} userId={user?.id} onChanged={async () => { await loadVersions(); await loadItems(itemClass); }} />}

      {/* Version / import status */}
      <Card data-testid="ndis-version-card">
        <CardContent className="py-4 flex flex-wrap items-center gap-x-8 gap-y-2">
          {version ? (
            <>
              <div><p className="text-xs text-muted-foreground">Version</p><p className="font-medium">{version.version_label}</p></div>
              <div><p className="text-xs text-muted-foreground">Status</p>
                <Badge variant="outline" className="border-green-300 text-green-700 bg-green-50 gap-1"><ShieldCheck className="h-3 w-3" />{version.status}{version.immutable ? ' · immutable' : ''}</Badge>
              </div>
              <div><p className="text-xs text-muted-foreground">Items</p><p className="font-medium" data-testid="ndis-version-counts">{version.counts?.total} total · {version.counts?.current} current · {version.counts?.legacy} legacy</p></div>
              <div><p className="text-xs text-muted-foreground">Imported</p><p className="font-medium">{version.imported_at ? new Date(version.imported_at).toLocaleDateString() : '—'}</p></div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="ndis-no-version">No catalogue version imported yet.{isSuperAdmin ? ' Use “Import / Re-check master”.' : ''}</p>
          )}
        </CardContent>
      </Card>

      {/* Current / Legacy + filters */}
      <div className="flex flex-col gap-4">
        <Tabs value={itemClass} onValueChange={setItemClass}>
          <TabsList data-testid="ndis-class-tabs">
            <TabsTrigger value="CURRENT" data-testid="ndis-tab-current">Current</TabsTrigger>
            <TabsTrigger value="LEGACY" data-testid="ndis-tab-legacy">Legacy</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search number or name…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="ndis-search-input" />
          </div>
          <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="ndis-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All categories</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          <Select value={regGroup} onValueChange={setRegGroup}><SelectTrigger data-testid="ndis-filter-reggroup"><SelectValue placeholder="Registration group" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All reg. groups</SelectItem>{regGroups.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          <Select value={unit} onValueChange={setUnit}><SelectTrigger data-testid="ndis-filter-unit"><SelectValue placeholder="Unit" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All units</SelectItem>{units.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          <Select value={pricing} onValueChange={setPricing}><SelectTrigger data-testid="ndis-filter-pricing"><SelectValue placeholder="Pricing type" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All pricing</SelectItem><SelectItem value="Priced">Priced</SelectItem><SelectItem value="Quotable">Quotable</SelectItem><SelectItem value="Unit Price = $1">Unit Price = $1</SelectItem></SelectContent></Select>
          <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="ndis-filter-status"><SelectValue placeholder="Effective status" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All statuses</SelectItem><SelectItem value="ACTIVE">Active</SelectItem><SelectItem value="FUTURE">Future</SelectItem><SelectItem value="EXPIRED">Expired</SelectItem></SelectContent></Select>
        </div>
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span data-testid="ndis-result-count">{loading ? 'Loading…' : `${filtered.length} item(s)`}</span>
          <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="ndis-reset-filters">Reset filters</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : error ? (
            <div className="p-10 text-center" data-testid="ndis-error-state">
              <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-2" />
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => loadItems(itemClass)}>Retry</Button>
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={Package} title="No items match your filters" description="Try adjusting the search or filters." testid="ndis-empty-state" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item Number</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Pricing</TableHead>
                  <TableHead className="text-right">National</TableHead>
                  <TableHead className="text-right">Remote</TableHead>
                  <TableHead className="text-right">Very Remote</TableHead>
                  <TableHead>Effective</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((it) => (
                  <TableRow key={it.id} data-testid={`ndis-row-${it.support_item_number}`} className="hover:bg-muted/60">
                    <TableCell className="font-mono text-xs">{it.support_item_number}</TableCell>
                    <TableCell className="max-w-md"><span className="line-clamp-2">{it.support_item_name}</span>
                      <span className="block text-[11px] text-muted-foreground">{it.support_category_name}</span></TableCell>
                    <TableCell>{it.unit || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{pricingType(it)}</Badge></TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{money(it.price_caps?.National)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{money(it.price_caps?.Remote)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{money(it.price_caps?.['Very Remote'])}</TableCell>
                    <TableCell className="text-xs">{it.start_date || '—'} → {it.end_date_open_ended ? 'open' : (it.end_date || '—')}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        effStatus(it) === 'ACTIVE' ? 'border-green-300 text-green-700 bg-green-50'
                          : effStatus(it) === 'FUTURE' ? 'border-blue-300 text-blue-700 bg-blue-50'
                            : 'border-muted-foreground/30 text-muted-foreground'}>{effStatus(it)}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {!loading && !error && filtered.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="ndis-prev-page"><ChevronLeft className="h-4 w-4" /> Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="ndis-next-page">Next <ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
